'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@/components/ui/Button'
import Field from '../ui/Field'

const ContactFormSchema = z.object({
    name: z.string().min(2, 'Required'),
    email: z.string().email('Invalid email'),
    company: z.string().min(1, 'Required'),
    message: z.string().min(10, 'Required'),
    confirm: z.boolean().refine((v) => v === true, { message: 'Required' }),
    website: z.string().max(0).optional().or(z.literal(''))
})

type ContactFormValues = z.infer<typeof ContactFormSchema>
type SubmitState = 'idle' | 'loading' | 'success' | 'error'

export default function ContactSection() {
    const [state, setState] = useState<SubmitState>('idle')
    const [startedAt] = useState(() => Date.now())

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<ContactFormValues>({
        resolver: zodResolver(ContactFormSchema),
        defaultValues: { website: '', confirm: false }
    })

    async function onSubmit(values: ContactFormValues) {
        setState('loading')
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ ...values, startedAt })
            })
            if (!res.ok) throw new Error('Request failed')
            setState('success')
        } catch {
            setState('error')
        }
    }

    return (
        <section id='contact' className='bg-page text-text'>
            <div className='container-x section-pad'>
                <div className='mx-auto max-w-205'>
                    <p className='text-xs font-semibold tracking-[0.22em] text-muted text-center'>CONTACT</p>
                    <h2 className='mt-3 text-balance text-center text-4xl font-semibold tracking-tight sm:text-5xl'>
                        Book a call
                    </h2>

                    <div className='mt-10 surface-strong p-7'>
                        {state === 'success' ? (
                            <div className='text-center'>
                                <p className='text-lg font-semibold tracking-tight'>Message received.</p>
                                <p className='mt-2 text-sm text-muted'>
                                    Thanks — you’ll get a confirmation email, and we’ll be in touch shortly.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4' noValidate>
                                {/* honeypot */}
                                <input
                                    type='text'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    className='hidden'
                                    aria-hidden='true'
                                    {...register('website')}
                                />

                                <div className='grid gap-4 sm:grid-cols-2'>
                                    <Field
                                        placeholder='Name'
                                        ariaLabel='Name'
                                        error={errors.name?.message}
                                        {...register('name')}
                                    />
                                    <Field
                                        placeholder='Email'
                                        ariaLabel='Email'
                                        error={errors.email?.message}
                                        type='email'
                                        inputMode='email'
                                        {...register('email')}
                                    />
                                </div>

                                <Field
                                    placeholder='Company'
                                    ariaLabel='Company'
                                    error={errors.company?.message}
                                    {...register('company')}
                                />

                                <Field
                                    as='textarea'
                                    placeholder='Message'
                                    ariaLabel='Message'
                                    error={errors.message?.message}
                                    rows={6}
                                    {...register('message')}
                                />

                                <label className='flex cursor-pointer items-start gap-3 rounded-lg bg-white p-4 shadow-sm'>
                                    <input
                                        type='checkbox'
                                        className='mt-1 h-4 w-4 accent-accent'
                                        {...register('confirm')}
                                        aria-label='Confirm consent'
                                    />
                                    <span className='text-sm text-muted'>
                                        I confirm that my information may be used to respond to this inquiry.
                                    </span>
                                </label>

                                {errors.confirm?.message ? (
                                    <p className='text-xs text-muted'>{errors.confirm.message}</p>
                                ) : null}

                                {state === 'error' ? (
                                    <p className='text-sm text-muted text-center'>
                                        Something went wrong. Please try again later.
                                    </p>
                                ) : null}

                                <div className='pt-2'>
                                    <Button
                                        type='submit'
                                        variant='cta'
                                        disabled={state === 'loading'}
                                        className='w-full'>
                                        {state === 'loading' ? 'Sending…' : 'Send'}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
